import React from "react";
import { motion } from "framer-motion";

const About = () => {
  const stats = [
    { number: "15+", label: "Years of Expertise" },
    { number: "30+", label: "Non-Profits Supported" },
    { number: "70+", label: "Trusted Sponsors" },
    { number: "100+", label: "Trusted Vendors" },
  ];

  const values = [
    {
      title: "Our Story",
      description:
        "While 3E Entertainment was officially established in 2021, our roots go much deeper. Our leadership has been shaping the Nepali entertainment landscape in the United States since 2000, building trust, producing landmark events, and creating platforms for artists and communities.",
      image: "/images/R5BI6429.webp",
      accentColor: "#C72631",
    },
    {
      title: "Our Mission",
      description:
        "We exist to promote Nepal on a global stage, preserve Nepali culture, language, music, and traditions, and strengthen the connection between Nepal and the Nepali diaspora in the United States.",
      image: "/images/ab-2.jpg",
      accentColor: "#C72631",
    },
    {
      title: "Why We Exist",
      description:
        "Every experience we create is designed to spark belonging, celebrate identity, and leave an impact far beyond the stage. We build lasting bridges across generations and communities.",
      image: "/images/asc3.jpg",
      accentColor: "#C72631",
    },
  ];

  const expertise = [
    {
      title: "Event Planning",
      subtitle: "Excellence in Execution",
      description: "Meticulous attention to every detail of your cultural celebration",
    },
    {
      title: "Cultural Curation",
      subtitle: "Authentic Experiences",
      description: "Preserving traditions while creating memorable moments",
    },
    {
      title: "Community Building",
      subtitle: "Strengthening Bonds",
      description: "Connecting diaspora communities across generations",
    },
    {
      title: "Artist Management",
      subtitle: "Talent Showcase",
      description: "Platform for artists to share their cultural craft",
    },
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-overlay" />
        <div className="hero-pattern" />
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            More Than an Event Company
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            A cultural platform dedicated to preserving heritage, inspiring
            pride, and creating meaningful experiences across America.
          </motion.p>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <span className="stat-number">{stat.number}</span>
              <span className="stat-label">{stat.label}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* About Introduction */}
      <section className="about-intro-section">
        <div className="intro-container">
          <div className="intro-content">
            <motion.div
              className="intro-text"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>About 3E Entertainment</h2>
              <div className="intro-cards">
                <div className="intro-card">
                  <div className="card-accent" />
                  <p>
                    <strong>3E Entertainment</strong> is more than an event
                    company. We are a cultural platform dedicated to preserving
                    heritage, inspiring pride, and creating meaningful
                    experiences that connect Nepali communities across America.
                  </p>
                </div>
                <div className="intro-card">
                  <div className="card-accent" />
                  <p>
                    At the core of everything we do are the 3Es:{" "}
                    <strong>Encourage</strong>, <strong>Engage</strong>, and{" "}
                    <strong>Empower</strong>. These are not just words. They
                    shape every concert, festival, tour, and community
                    initiative we bring to life.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="intro-image"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="image-wrapper">
                <img src="/images/about-2.jpg" alt="Nepal Mountain Landscape" loading="lazy" decoding="async" />
                <div className="image-overlay">
                  <span>Celebrating Nepal's Rich Heritage</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="section-header">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
          >
            Our Story, Mission & Approach
          </motion.h2>
        </div>

        <div className="values-grid">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              className="value-card"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              viewport={{ once: true }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
            >
              <div className="value-image">
                <img src={value.image} alt={value.title} loading="lazy" decoding="async" />
                <div className="value-image-overlay" />
                <div
                  className="value-accent-bar"
                  style={{ background: value.accentColor }}
                />
              </div>
              <div className="value-content">
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Expertise Section */}
      <section className="expertise-section">
        <div className="expertise-container">
          <div className="section-header light">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              What We Bring to Every Event
            </motion.h2>
          </div>

          <div className="expertise-grid">
            {expertise.map((item, index) => (
              <motion.div
                key={item.title}
                className="expertise-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="expertise-number">0{index + 1}</div>
                <h4>{item.title}</h4>
                <span className="expertise-subtitle">{item.subtitle}</span>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-overlay" />
        <motion.div
          className="cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
          >
            Ready to Celebrate Your Heritage?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            Join us in creating unforgettable cultural experiences that bring
            communities together and keep traditions alive.
          </motion.p>
          <motion.div
            className="cta-buttons"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            <a href="/contact" className="cta-btn primary">
              Get In Touch
              <span className="btn-arrow">→</span>
            </a>
            <a href="/services" className="cta-btn secondary">
              Explore Our Services
            </a>
          </motion.div>
        </motion.div>
      </section>

      <style>{`
        .about-page {
          overflow-x: hidden;
        }

        /* Hero Section */
        .about-hero {
          position: relative;
          min-height: 80vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: url("/images/about-hero.jpg") center/cover no-repeat;
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 900px;
          padding: 0 2rem;
        }


        .hero-content h1 {
          font-size: clamp(2.5rem, 6vw, 4rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1.5rem;
          line-height: 1.1;
        }

        .hero-content p {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.7;
          max-width: 600px;
          margin: 0 auto;
        }

        /* Stats Section */
        .stats-section {
          background: #fff;
          padding: 0;
          margin-top: -60px;
          position: relative;
          z-index: 10;
        }

        .stats-container {
          max-width: 1000px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .stat-card {
          padding: 2.5rem 1.5rem;
          text-align: center;
          border-right: 1px solid #f0f0f0;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .stat-card:last-child {
          border-right: none;
        }

        .stat-card:hover {
          background: #fafafa;
        }

        .stat-number {
          display: block;
          font-size: 2.5rem;
          font-weight: 900;
          color: #C72631;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          font-size: 0.85rem;
          color: #666;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* About Intro Section */
        .about-intro-section {
          padding: 8rem 2rem;
          background: #fff;
        }

        .intro-container {
          max-width: 1300px;
          margin: 0 auto;
        }

        .intro-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
        }



        .intro-text h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 2rem;
        }

        .intro-cards {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .intro-card {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
          position: relative;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          border: 1px solid #f0f0f0;
        }

        .intro-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
          border-color: transparent;
        }

        .card-accent {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #C72631;
        }

        .intro-card p {
          color: #555;
          font-size: 1.05rem;
          line-height: 1.7;
          margin: 0;
        }

        .intro-card strong {
          color: #C72631;
        }

        .intro-image {
          position: relative;
        }

        .image-wrapper {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .image-wrapper img {
          width: 100%;
          height: 450px;
          object-fit: cover;
          display: block;
        }

        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 2rem;
          background: rgba(10, 10, 10, 0.7);
        }

        .image-overlay span {
          color: white;
          font-weight: 600;
          font-size: 1rem;
        }

        /* Values Section */
        .values-section {
          padding: 6rem 2rem;
          background: #f7f7f7;
        }

        .section-header {
          text-align: center;
          max-width: 700px;
          margin: 0 auto 4rem;
        }

        .section-header h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0;
        }

        .section-header.light h2 {
          color: white;
        }

        .values-grid {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
        }

        .value-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
          transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
          border: 1px solid #f0f0f0;
        }

        .value-card:hover {
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12);
          border-color: transparent;
        }

        .value-image {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .value-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .value-card:hover .value-image img {
          transform: scale(1.05);
        }

        .value-image-overlay {
          display: none;
        }

        .value-accent-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 4px;
        }

        .value-content {
          padding: 2rem;
        }

        .value-content h3 {
          font-size: 1.3rem;
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 1rem;
        }

        .value-content p {
          color: #666;
          line-height: 1.7;
          margin: 0;
          font-size: 0.95rem;
        }

        /* Expertise Section */
        .expertise-section {
          padding: 6rem 2rem;
          background: #0a0a0a;
        }

        .expertise-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .expertise-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
        }

        .expertise-card {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .expertise-card:hover {
          background: rgba(255, 255, 255, 0.08);
          transform: translateY(-5px);
        }

        .expertise-number {
          font-size: 2rem;
          font-weight: 900;
          color: #C72631;
          margin-bottom: 1rem;
        }

        .expertise-card h4 {
          color: white;
          font-size: 1.1rem;
          font-weight: 700;
          margin: 0 0 0.5rem;
        }

        .expertise-subtitle {
          display: block;
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 1rem;
        }

        .expertise-card p {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.9rem;
          line-height: 1.6;
          margin: 0;
        }

        /* CTA Section */
        .cta-section {
          position: relative;
          padding: 8rem 2rem;
          background: url("/images/about-2.jpg") center/cover no-repeat;
          text-align: center;
        }

        .cta-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .cta-content {
          position: relative;
          z-index: 2;
          max-width: 700px;
          margin: 0 auto;
        }

        .cta-content h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1.5rem;
        }

        .cta-content p {
          font-size: 1.15rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.7;
          margin: 0 0 2.5rem;
        }

        .cta-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .cta-btn.primary {
          background: white;
          color: #C72631;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        }

        .cta-btn.primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
        }

        .btn-arrow {
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .cta-btn.primary:hover .btn-arrow {
          transform: translateX(5px);
        }

        .cta-btn.secondary {
          background: transparent;
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.4);
        }

        .cta-btn.secondary:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: white;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .intro-content {
            grid-template-columns: 1fr;
            gap: 3rem;
          }

          .values-grid {
            grid-template-columns: 1fr;
            max-width: 500px;
          }

          .expertise-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .stats-container {
            grid-template-columns: repeat(2, 1fr);
            margin: 0 1rem;
          }

          .stat-card {
            padding: 2rem 1rem;
          }

          .stat-card:nth-child(2) {
            border-right: none;
          }

          .stat-card:nth-child(1),
          .stat-card:nth-child(2) {
            border-bottom: 1px solid #f0f0f0;
          }

          .about-intro-section,
          .values-section,
          .expertise-section,
          .cta-section {
            padding: 4rem 1.5rem;
          }

          .image-wrapper img {
            height: 350px;
          }
        }

        @media (max-width: 480px) {
          .stats-container {
            grid-template-columns: 1fr;
          }

          .stat-card {
            border-right: none;
            border-bottom: 1px solid #f0f0f0;
          }

          .stat-card:last-child {
            border-bottom: none;
          }

          .expertise-grid {
            grid-template-columns: 1fr;
          }

          .cta-buttons {
            flex-direction: column;
          }

          .cta-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default About;
